export type FinalSaleState = "TRUE" | "FALSE" | "UNKNOWN";

export type FinalSaleFact = {
  ready: boolean;
  value: boolean | null;
  status: FinalSaleState | "PARTIAL" | "UNAVAILABLE";
  errors: string[];
  categoryId: string | number | null;
};

// One verified identifier from the bridge: the digits, their kind (UPC_A,
// EAN_13, ...) and the OpenCart field they came from (UPC, EAN, JAN, ISBN).
export type GtinFact = {
  value: string;
  type: string;
  source: string;
};

export type MerchantFact = {
  ready: boolean;
  status: "READY" | "PARTIAL" | "UNAVAILABLE";
  errors: string[];
  gtin: string;
  gtinStatus: string;
  gtinType: string;
  gtins: GtinFact[];
  nativeMpn: string;
  mpnStatus: string;
};

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return value && typeof value === "object" ? (value as UnknownRecord) : null;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function errors(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

function gtins(value: unknown): GtinFact[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const fact = record(entry);
    const digits = text(fact?.value);
    return fact && digits
      ? [{ value: digits, type: text(fact.type), source: text(fact.source) }]
      : [];
  });
}

export function unavailableFinalSaleFact(): FinalSaleFact {
  return {
    ready: false,
    value: null,
    status: "UNKNOWN",
    errors: ["UNAVAILABLE"],
    categoryId: null,
  };
}

export function unavailableMerchantFact(): MerchantFact {
  return {
    ready: false,
    status: "UNAVAILABLE",
    errors: ["UNAVAILABLE"],
    gtin: "",
    gtinStatus: "UNKNOWN",
    gtinType: "",
    gtins: [],
    nativeMpn: "",
    mpnStatus: "UNKNOWN",
  };
}

/** Preserve only a consistent typed final-sale result; all other inputs fail closed. */
export function normalizeFinalSaleFact(value: unknown): FinalSaleFact {
  const fact = record(value);
  if (!fact) return unavailableFinalSaleFact();
  if (fact.ready === true && fact.status === "TRUE" && fact.value === true) {
    return {
      ready: true,
      value: true,
      status: "TRUE",
      errors: errors(fact.errors),
      categoryId:
        typeof fact.categoryId === "string" ||
        typeof fact.categoryId === "number"
          ? fact.categoryId
          : null,
    };
  }
  if (fact.ready === true && fact.status === "FALSE" && fact.value === false) {
    return {
      ready: true,
      value: false,
      status: "FALSE",
      errors: errors(fact.errors),
      categoryId:
        typeof fact.categoryId === "string" ||
        typeof fact.categoryId === "number"
          ? fact.categoryId
          : null,
    };
  }
  return unavailableFinalSaleFact();
}

export function finalSaleState(value: unknown): FinalSaleState {
  const fact = normalizeFinalSaleFact(value);
  return fact.status === "TRUE" || fact.status === "FALSE"
    ? fact.status
    : "UNKNOWN";
}

/** Keep bridge identifiers only when their enclosing merchant snapshot is usable. */
export function normalizeMerchantFact(value: unknown): MerchantFact {
  const merchant = record(value);
  if (
    !merchant ||
    (merchant.status !== "READY" && merchant.status !== "PARTIAL")
  ) {
    return unavailableMerchantFact();
  }
  return {
    ready: merchant.ready === true,
    status: merchant.status,
    errors: errors(merchant.errors),
    gtin: text(merchant.gtin),
    gtinStatus: text(merchant.gtinStatus),
    gtinType: text(merchant.gtinType),
    gtins: gtins(merchant.gtins),
    nativeMpn: text(merchant.nativeMpn),
    mpnStatus: text(merchant.mpnStatus),
  };
}

// The fields the product page's withSeoBridgeProduct() sets on a product
// snapshot from the SEO bridge answer.
const SEO_BRIDGE_PRODUCT_KEYS = [
  "robots",
  "breadcrumbs",
  "finalSale",
  "merchant",
  "__finalSalePending",
] as const;

function stableJson(value: unknown): string {
  if (value === undefined) return "undefined";
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  const source = value as Record<string, unknown>;
  return `{${Object.keys(source)
    .filter((key) => source[key] !== undefined)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJson(source[key])}`)
    .join(",")}}`;
}

/**
 * True when `next` (the shown product with the bridge answer applied) carries
 * exactly the SEO-bridge facts `current` already has. A landing page usually
 * gets the same facts the server rendered, and re-committing an equal product
 * only re-renders the whole PDP.
 */
export function sameSeoBridgeProductFacts(
  current: unknown,
  next: unknown
): boolean {
  const a = record(current);
  const b = record(next);
  if (!a || !b) return false;
  return SEO_BRIDGE_PRODUCT_KEYS.every(
    (key) => stableJson(a[key]) === stableJson(b[key])
  );
}
